/**
 * Firestore implementation of ReceiptRepository.
 *
 * Path: receipts/{id}
 *
 * Submitted receipts — staff-uploaded evidence that finance assembles into
 * claims. See ADR 0005 Revision 1 and src/types/receipt.ts.
 */

import {
  collection,
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import type { Receipt, EntityId } from '../../../types';
import type { ReceiptRepository } from '../types';

const COLLECTION = 'receipts';

export const firestoreReceiptRepository: ReceiptRepository = {
  async findById(id: EntityId): Promise<Receipt | null> {
    const snap = await getDoc(doc(db, COLLECTION, id));
    return snap.exists() ? (snap.data() as Receipt) : null;
  },

  async save(entity: Receipt): Promise<void> {
    await setDoc(doc(db, COLLECTION, entity.id), entity);
  },

  async delete(id: EntityId): Promise<void> {
    await deleteDoc(doc(db, COLLECTION, id));
  },

  async list(): Promise<Receipt[]> {
    const snap = await getDocs(collection(db, COLLECTION));
    return snap.docs.map((d) => d.data() as Receipt);
  },

  async findBySubmitter(submitterEmail: string): Promise<Receipt[]> {
    const q = query(collection(db, COLLECTION), where('submittedBy', '==', submitterEmail));
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data() as Receipt);
  },

  async findByStatus(status: Receipt['status']): Promise<Receipt[]> {
    const q = query(collection(db, COLLECTION), where('status', '==', status));
    const snap = await getDocs(q);
    return snap.docs.map((d) => d.data() as Receipt);
  },
};
